import { useState, useEffect } from "react";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { PlusCircle, Pencil, Trash2, Search } from "lucide-react";
import MastersLayout from "@/components/masters/MastersLayout";
import TablePagination from "@/components/masters/TablePagination";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/modal";
import { Switch } from "@/components/ui/switch";

// Initial master accounts data
const INITIAL_ACCOUNTS = [
  {
    id: 1,
    name: "Primary Account",
    code: "PA001",
    group: "Residential",
    status: "Active",
  },
  {
    id: 2,
    name: "Business Account",
    code: "BA001",
    group: "Commercial",
    status: "Active",
  },
  {
    id: 3,
    name: "Government Account",
    code: "GA001",
    group: "Government",
    status: "Active",
  },
];

const ITEMS_PER_PAGE = 10;

// Add these constants after the INITIAL_MASTER_ACCOUNTS
const MASTER_ACCOUNT_TYPES = [
  "Asset",
  "Liability",
  "Equity",
  "Revenue",
  "Expense",
];
const MASTER_ACCOUNT_STATUSES = ["Active", "Inactive"];

const MasterAccountPage = () => {
  const [accounts, setAccounts] = useState(INITIAL_ACCOUNTS);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    code: "",
    group: "",
    status: "Active",
  });
  const [editingId, setEditingId] = useState<number | null>(null);
  const [currentPage, setCurrentPage] = useState(1);

  // Get the groups from local storage or use the default ones
  const [groups, setGroups] = useState<Array<{ id: number; name: string }>>([]);

  const { toast } = useToast();

  // Load data from localStorage on initial render
  useEffect(() => {
    const savedAccounts = localStorage.getItem("manterflow-master-accounts");
    if (savedAccounts) {
      setAccounts(JSON.parse(savedAccounts));
    }

    // Load groups
    const storedGroups = localStorage.getItem("manterflow-groups");
    if (storedGroups) {
      setGroups(JSON.parse(storedGroups));
    } else {
      setGroups([
        { id: 1, name: "Residential" },
        { id: 2, name: "Commercial" },
        { id: 3, name: "Industrial" },
        { id: 4, name: "Government" },
      ]);
    }
  }, []);

  const handleInputChange = (field: string, value: string) => {
    setFormData({ ...formData, [field]: value });
  };

  const handleEdit = (account: (typeof accounts)[0]) => {
    setFormData({
      name: account.name,
      code: account.code,
      group: account.group,
      status: account.status || "Active",
    });
    setEditingId(account.id);
    setShowForm(true);
  };

  const handleDelete = (id: number) => {
    const updatedAccounts = accounts.filter((account) => account.id !== id);
    setAccounts(updatedAccounts);

    // Save to local storage
    localStorage.setItem(
      "manterflow-master-accounts",
      JSON.stringify(updatedAccounts)
    );

    toast({
      title: "Account Deleted",
      description: "The account has been deleted successfully.",
    });

    // Adjust current page if necessary
    const totalPages = Math.ceil(updatedAccounts.length / ITEMS_PER_PAGE);
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(totalPages);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (editingId !== null) {
      // Update existing account
      const updatedAccounts = accounts.map((account) =>
        account.id === editingId ? { ...account, ...formData } : account
      );

      setAccounts(updatedAccounts);
      localStorage.setItem(
        "manterflow-master-accounts",
        JSON.stringify(updatedAccounts)
      );

      toast({
        title: "Account Updated",
        description: `${formData.name} has been updated successfully.`,
      });
    } else {
      // Add new account
      const newAccount = {
        id:
          accounts.length > 0
            ? Math.max(...accounts.map((account) => account.id)) + 1
            : 1,
        ...formData,
      };

      const updatedAccounts = [...accounts, newAccount];
      setAccounts(updatedAccounts);
      localStorage.setItem(
        "manterflow-master-accounts",
        JSON.stringify(updatedAccounts)
      );

      toast({
        title: "Account Added",
        description: `${formData.name} has been added successfully.`,
      });

      // Navigate to the last page where the new item appears
      const totalPages = Math.ceil(updatedAccounts.length / ITEMS_PER_PAGE);
      setCurrentPage(totalPages);
    }

    // Reset form and hide it
    setFormData({
      name: "",
      code: "",
      group: "",
      status: "Active",
    });
    setEditingId(null);
    setShowForm(false);
  };

  // Pagination calculations
  const totalPages = Math.max(1, Math.ceil(accounts.length / ITEMS_PER_PAGE));
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = Math.min(startIndex + ITEMS_PER_PAGE, accounts.length);
  const currentItems = accounts.slice(startIndex, endIndex);

  const [filters, setFilters] = useState({
    type: "all",
    status: "all",
    search: "",
  });

  // Add this after other state declarations
  const filteredMasterAccounts = accounts.filter((account) => {
    const typeMatch = filters.type === "all" || account.group === filters.type;
    const statusMatch =
      filters.status === "all" || account.group === filters.status;
    const searchMatch =
      !filters.search ||
      account.name.toLowerCase().includes(filters.search.toLowerCase()) ||
      account.code.toLowerCase().includes(filters.search.toLowerCase()) ||
      account.group.toLowerCase().includes(filters.search.toLowerCase());

    return typeMatch && statusMatch && searchMatch;
  });

  // Update pagination calculations to use filteredMasterAccounts
  const indexOfLastItem = currentPage * ITEMS_PER_PAGE;
  const indexOfFirstItem = indexOfLastItem - ITEMS_PER_PAGE;
  const currentItemsFiltered = filteredMasterAccounts.slice(
    indexOfFirstItem,
    indexOfLastItem
  );
  const totalPagesFiltered = Math.ceil(
    filteredMasterAccounts.length / ITEMS_PER_PAGE
  );

  // Add this function to handle filter changes
  const handleFilterChange = (field: string, value: string) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
    setCurrentPage(1); // Reset to first page when filters change
  };

  return (
    <MastersLayout
      title="Master Accounts"
      description="Manage master accounts and their configurations"
    >
      <div className="space-y-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search master accounts..."
                value={filters.search}
                onChange={(e) => handleFilterChange("search", e.target.value)}
                className="pl-8"
              />
            </div>
          </div>
          <div className="flex gap-4">
            <Select
              value={filters.type}
              onValueChange={(value) => handleFilterChange("type", value)}
            >
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="All Types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                {MASTER_ACCOUNT_TYPES.map((type) => (
                  <SelectItem key={type} value={type}>
                    {type}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select
              value={filters.status}
              onValueChange={(value) => handleFilterChange("status", value)}
            >
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="All Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                {MASTER_ACCOUNT_STATUSES.map((status) => (
                  <SelectItem key={status} value={status}>
                    {status}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Button
              variant="outline"
              onClick={() => {
                setFilters({ type: "all", status: "all", search: "" });
                setCurrentPage(1);
              }}
            >
              Reset
            </Button>
          </div>
        </div>

        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Master Accounts</CardTitle>
                <CardDescription>
                  Manage master accounts and their configurations
                </CardDescription>
              </div>
              <Button onClick={() => setShowForm(true)}>
                <PlusCircle className="mr-2 h-4 w-4" />
                Add Master Account
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Code</TableHead>
                  <TableHead>Group</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="w-[150px] text-right">
                    Actions
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {currentItemsFiltered.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={6}
                      className="text-center py-4 text-muted-foreground"
                    >
                      {filters.search || filters.type || filters.status
                        ? "No master accounts match the selected filters"
                        : "No master accounts found"}
                    </TableCell>
                  </TableRow>
                ) : (
                  currentItemsFiltered.map((account) => (
                    <TableRow key={account.id}>
                      <TableCell className="font-medium">
                        {account.name}
                      </TableCell>
                      <TableCell>{account.code}</TableCell>
                      <TableCell>{account.group}</TableCell>
                      <TableCell>
                        <span
                          className={`inline-block px-2 py-1 text-xs rounded-full ${
                            account.status === "Active"
                              ? "bg-green-100 text-green-800"
                              : "bg-gray-100 text-gray-800"
                          }`}
                        >
                          {account.status || "Active"}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEdit(account)}
                          >
                            <Pencil className="h-4 w-4" />
                            <span className="sr-only">Edit</span>
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(account.id)}
                            className="text-destructive hover:text-destructive/90 hover:bg-destructive/10"
                          >
                            <Trash2 className="h-4 w-4" />
                            <span className="sr-only">Delete</span>
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>

            {filteredMasterAccounts.length > ITEMS_PER_PAGE && (
              <TablePagination
                currentPage={currentPage}
                totalPages={totalPagesFiltered}
                onPageChange={setCurrentPage}
              />
            )}
          </CardContent>
        </Card>
      </div>

      {showForm && (
        <Dialog open={showForm} onOpenChange={setShowForm}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                {editingId ? "Edit" : "Add New"} Account
              </DialogTitle>
              <DialogDescription>
                {editingId
                  ? "Update the master account details"
                  : "Create a new master account"}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit}>
              <div className="space-y-6 py-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Account Name</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) =>
                        handleInputChange("name", e.target.value)
                      }
                      placeholder="Enter account name"
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="code">Account Code</Label>
                    <Input
                      id="code"
                      value={formData.code}
                      onChange={(e) =>
                        handleInputChange("code", e.target.value)
                      }
                      placeholder="Enter account code"
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="group">Group</Label>
                    <Select
                      value={formData.group}
                      onValueChange={(value) =>
                        handleInputChange("group", value)
                      }
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select group" />
                      </SelectTrigger>
                      <SelectContent>
                        {groups.map((group) => (
                          <SelectItem key={group.id} value={group.name}>
                            {group.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="status">Status</Label>
                    <div className="flex items-center gap-2">
                      <Switch
                        id="status"
                        checked={formData.status === "Active"}
                        onCheckedChange={(checked) =>
                          handleInputChange(
                            "status",
                            checked ? "Active" : "Inactive"
                          )
                        }
                      />
                      <span>{formData.status}</span>
                    </div>
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button
                  variant="outline"
                  type="button"
                  onClick={() => {
                    setShowForm(false);
                    setFormData({
                      name: "",
                      code: "",
                      group: "",
                      status: "Active",
                    });
                    setEditingId(null);
                  }}
                >
                  Cancel
                </Button>
                <Button type="submit">
                  {editingId ? "Update" : "Create"} Account
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      )}
    </MastersLayout>
  );
};

export default MasterAccountPage;
